import { useQuery } from "@tanstack/react-query";

import { ReactComponent as OutlineChevronDown } from "assets/svg/outline-chevron-down.svg";
import { ReactComponent as OutlineDocumentFilled } from "assets/svg/outline-document-filled.svg";

import ScheduleSessionModal from "components/ai-home/components/schedule-session/ScheduleSessionModal";
import ConfirmModal from "components/confirm-modal";
import DataProcessingModal from "components/data-processing-modal/DataProcessingModal";
import DropdownToggle from "components/dropdown-toggle/DropdownToggle";
import Loading from "components/Loading";
import ErrorBanner from "components/ai-session-review/ErrorBanner";
import useExtendUserActivity from "hooks/useExtendUserActivity";
import { listSchemasQueryOptions } from "queries/schemaQueryOptions";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Dropdown } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import proCreateOrUpdatePatientConsent from "services/patient-consent/proCreateOrUpdatePatientConsent";
import proQueryPatientConsent from "services/patient-consent/proQueryPatientConsent";
import attachNoteToAppointment from "services/scheduler/attachNoteToAppointment";
// import RouteLeavingGuard from "components/Dashboard/DoctorDashboard/notes-and-forms/scenes/route-leaving-guard";
import styles from "./AudioRecorder.module.scss";
import useAudioUploadStream from "./useAudioUploadStream";
import { fetchOrchidAPI } from "utils/api";
import AudioRecorderControlPanel from "./controlPanel/AudioRecorderControlPanel";
import ExternalAndInPersonModeRecorder from "./recorderMode/ExternalAndInPersonMode";
import moment from "moment-timezone";


const getCurrentTime = () => {
  const now = moment();
  const time = now.format("h:mm A");
  const timeZoneAbbr = now.tz(moment.tz.guess()).format("z");

  return `${time} ${timeZoneAbbr}`.toUpperCase();
};

const AudioRecorder = () => {
  useExtendUserActivity();

  const location = useLocation();
  const { appointmentId, sessionSource } = useMemo(() => {
    const query = new URLSearchParams(location.search);
    return {
      appointmentId: query.get("appointment_id") || "",
      sessionSource: query.get("session-source") || "",
    };
  }, [location.search]);

  const [appointment, setAppointment] = useState<AppointmentMetaData>();

  useEffect(() => {
    const fetchAppointment = async () => {
      try {
        const response = await fetchOrchidAPI(
          "/api/scheduler/v1/snapshot/appointment",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              appointment_ids: [parseInt(appointmentId)],
            }),
          },
        );

        if (!response.ok) {
          throw new Error("Failed to fetch appointment");
        }

        const data = await response.json();
        setAppointment(data.appointments[0]);
      } catch (error) {
        console.error("Error fetching appointment:", error);
      }
    };

    if (appointmentId) {
      fetchAppointment();
    }
  }, [appointmentId]);

  const [showNotesPanel, setShowNotesPanel] = useState(false);
  const [showConfirmEndModal, setShowConfirmEndModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);

  const [showConfirmStoreModal, setShowConfirmStoreModal] = useState(false);
  const [orchidPolicyConsented, setOrchidPolicyConsented] = useState(true);

  const [isPaused, setIsPaused] = useState(false);

  const [currentTime, setCurrentTime] = useState(getCurrentTime());
  const [secondsRecorded, setSecondsRecorded] = useState<number>(0);
  const navigate = useNavigate();
  const [isBackupWarningDismissed, setIsBackupWarningDismissed] =
    useState(false);

  const recordingTimeString = useMemo(() => {
    const hours = Math.floor(secondsRecorded / 3600);
    const minutes = Math.floor((secondsRecorded % 3600) / 60)
      .toString()
      .padStart(2, "0");
    const seconds = (secondsRecorded % 60).toString().padStart(2, "0");
    return `${hours}:${minutes}:${seconds}`;
  }, [secondsRecorded]);

  const [uploadError, setUploadError] = useState<unknown>();
  const [recordingError, setRecordingError] = useState<unknown>();

  const [patientAudioSource, setPatientAudioSource] = useState<
    "microphone" | "tab"
  >("microphone");

  const [showBrowserErrorBanner, setShowBrowserErrorBanner] = useState(false);
  const [showTimeLimitWarning, setShowTimeLimitWarning] = useState(false);
  const [showEndSession, setShowEndSession] = useState(false);

  useEffect(() => {
    window.onbeforeunload = () =>
      "Some parts of your recording will be lost if you leave the page now";
    return () => {
      window.onbeforeunload = null;
    };
  });

  useEffect(() => {
    if (!appointment?.patient?.orchid_user_id) {
      return;
    }

    proQueryPatientConsent(appointment.patient.orchid_user_id).then(
      (response) => {
        if (response) {
          setOrchidPolicyConsented(response.orchid_policy_consented as boolean);
          setShowConfirmStoreModal(!response.ai_policy_consented);
        } else {
          setShowConfirmStoreModal(true);
        }
      },
    );
  }, [appointment?.patient?.orchid_user_id]);

  const onConfirmConsent = useCallback(async () => {
    if (!appointment?.patient?.orchid_user_id) {
      return;
    }
    try {
      await proCreateOrUpdatePatientConsent(
        appointment.patient.orchid_user_id,
        true,
        orchidPolicyConsented,
      );
      setShowConfirmStoreModal(false);
    } catch {
      console.error("Failed to set patient consent");
    }
  }, [orchidPolicyConsented, appointment?.patient?.orchid_user_id]);

  const proceedToReview = useCallback(
    () =>
      navigate(
        `/pro-dashboard/session-review/${appointmentId}?session-source=${
          sessionSource || "live-audio-recording"
        }&session-ended=${true}`,
        { replace: true }
      ),
    [appointmentId, navigate, sessionSource],
  );

  const secondsRecordedRef = useRef(secondsRecorded);
  const {
    isRecordingStarted,
    isRecordingFinished,
    mediaRecorderRef,
    startStreaming,
    finishStreaming,
    completeUpload,
    saveToDisk,
    getCurrentInputVolume,
    disconnectRecorderAndStream,
    micMediaStreamRef,
    hasMicroPhoneAccess,
    onAudioInputChange,
  } = useAudioUploadStream({
    appointment,
    secondsRecordedRef,
    setSecondsRecorded,
    onRecordingError: setRecordingError,
    onUploadError: setUploadError,
    onUploadFinished: proceedToReview,
  });
  const disconnectRef = useRef(disconnectRecorderAndStream);
  disconnectRef.current = disconnectRecorderAndStream;

  // Disconnecting the recorder and stream to prevent microphone access and unwanted uploads after the user moves away from the screen
  useEffect(() => {
    return () => {
      try {
        disconnectRef.current();
      } catch (error) {
        console.error("Failed to disconnect recorder and stream", error);
      }
    };
  }, []);

  const mode = useMemo(() => {
    if (appointment?.session_place_outside_of_orchid === "external-platform") {
      return "external";
    }

    return "in-person";
  }, [appointment]);

  const recordingAlreadyExistedAtStart = useMemo(
    () => !!appointment?.orchid_pro_left_audio && !isRecordingFinished,
    [appointment, isRecordingFinished],
  );

  // Prevent recording from starting twice
  const initializedStreaming = useRef(false);
  useEffect(() => {
    if (!appointment) {
      // Wait for appointment;
      return;
    }

    if (mode === "external") {
      // Only start recording automatically for in-person sessions
      return;
    }

    if (recordingAlreadyExistedAtStart) {
      return;
    }

    if (initializedStreaming.current) {
      return;
    }
    initializedStreaming.current = true;

    startStreaming({
      captureTabAudio: false,
    });
  }, [startStreaming, appointment, recordingAlreadyExistedAtStart, mode]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const updateSeconds = () => {
      setCurrentTime(getCurrentTime());

      if (mediaRecorderRef.current?.state === "recording") {
        setSecondsRecorded((prev) => {
          const next = prev + 1;
          secondsRecordedRef.current = Math.max(
            secondsRecordedRef.current,
            next,
          );
          return next;
        });
      }

      timeoutId = setTimeout(updateSeconds, 1000);
    };

    updateSeconds();

    return () => clearTimeout(timeoutId);
  }, [mediaRecorderRef]);

  const [noteTemplateSearch, setNoteTemplateSearch] = useState("");
  const noteSchemasQuery = useQuery(listSchemasQueryOptions("note"));
  const noteSchemas = useMemo(
    () => noteSchemasQuery.data ?? [],
    [noteSchemasQuery.data],
  );
  const [noteSchema, setNoteSchema] = useState<NoteSchema>();

  // useEffect(() => {
  //   setNoteSchema(noteSchemas.find((el) => el.title === "SOAP Note"));
  // }, [noteSchemas]);

  const filtered = useMemo(() => {
    if (!noteTemplateSearch) {
      return noteSchemas;
    }

    return noteSchemas.filter((el) =>
      el.title?.toLowerCase().includes(noteTemplateSearch.toLowerCase()),
    );
  }, [noteSchemas, noteTemplateSearch]);

  const currSelectedNoteSchema = noteSchemas.find(
    (el) => el.note_schema_id === noteSchema?.note_schema_id,
  );

  const onConfirmEndSession = useCallback(async () => {
    if (noteSchema && appointmentId && appointment?.patient?.orchid_user_id) {
      try {
        await attachNoteToAppointment({
          appointmentId: parseInt(appointmentId),
          patientId: appointment.patient.orchid_user_id,
          noteSchema,
        });
      } catch (error) {
        console.error("Failed to attach note to appointment", error);
      }
    }
    finishStreaming();
    setShowConfirmEndModal(false);
  }, [
    noteSchema,
    appointmentId,
    appointment?.patient?.orchid_user_id,
    finishStreaming,
  ]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    const checkAppointmentEnd = () => {
      if (appointment?.appointment_end) {
        const appointmentEndTime = new Date(appointment.appointment_end);
        const currentTime = new Date();
        const timeDifference =
          currentTime.getTime() - appointmentEndTime.getTime();
        const minutesDifference = timeDifference / (1000 * 60);

        if (minutesDifference > 45) {
          setShowEndSession(true);
          // Ending the session after showing the end popup for 1 min
          setTimeout(onConfirmEndSession, 60 * 1000);
          return;
        }
      }
      timeoutId = setTimeout(checkAppointmentEnd, 30 * 1000);
    };

    checkAppointmentEnd();

    return () => clearTimeout(timeoutId);
  }, [appointment, onConfirmEndSession]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const checkTimeLimit = () => {
      if (appointment?.appointment_end && !showEndSession) {
        const appointmentEndTime = new Date(appointment.appointment_end);
        const currentTime = new Date();
        const timeDifference =
          currentTime.getTime() - appointmentEndTime.getTime();
        const minutesDifference = timeDifference / (1000 * 60);

        if (minutesDifference > 40) {
          setShowTimeLimitWarning(true);
          return;
        }
      }
      timeoutId = setTimeout(checkTimeLimit, 60 * 1000);
    };

    checkTimeLimit();

    return () => {
      clearTimeout(timeoutId);
    };
  }, [appointment, showEndSession]);

  if (recordingAlreadyExistedAtStart) {
    return (
      <div className={styles["audio-recorder--already-finished"]}>
        The session has already been recorded.
      </div>
    );
  }

  // const shouldPrevent = isRecordingStarted && !isRecordingFinished;
  // Note: Navigation blocking (both hard refresh/close and programmatic) is handled by RouteLeavingGuard component

  return (
    <div className={styles["audio-recorder"]}>
      {/* <RouteLeavingGuard
        when={shouldPrevent}
        message="You are currently recording a session. If you leave now, some parts of your recording will be lost. Are you sure you want to continue?"
      /> */}
      <ScheduleSessionModal
        show={showScheduleModal}
        handleHide={() => setShowScheduleModal(false)}
        title={"Schedule a New Session"}
        buttonText={"Schedule"}
        allowRecordingUpload={false}
      />

      <ConfirmModal
        labelTitle={"Session Ended"}
        children={
          "The session has ended. Please confirm to proceed with the next steps."
        }
        labelConfirm={"Proceed"}
        hideCancel
        onCancel={() => setShowEndSession(false)}
        show={showEndSession}
        onConfirm={() => {
          setShowEndSession(false);
          onConfirmEndSession();
        }}
      />

      <ConfirmModal
        labelTitle={"Time Limit Warning"}
        children={
          "You have 5 minutes remaining in this recording session. Please wrap up your activities."
        }
        labelConfirm={"Okay"}
        hideCancel
        onCancel={() => setShowTimeLimitWarning(false)}
        show={showTimeLimitWarning && !showEndSession}
        onConfirm={() => setShowTimeLimitWarning(false)}
      />

      <ConfirmModal
        labelTitle={
          "Do you have the client’s consent to store this audio recording?"
        }
        children={
          "You need client consent to store this recording for clinical, training, or supervision purposes. You’ll still receive notes and insights even if you don’t store it."
        }
        labelConfirm={"Yes, Store it"}
        labelCancel={"No, Cancel"}
        show={showConfirmStoreModal}
        onCancel={() => setShowConfirmStoreModal(false)}
        onConfirm={() => onConfirmConsent()}
      />

      <ConfirmModal
        labelTitle={"End audio recording?"}
        children={
          <div>
            <div className={styles["confirm-end__timer"]}>
              {recordingTimeString}
            </div>
            <div className={styles["confirm-end__description"]}>
              Do you want to end session now? Orchid will provide AI notes and
              session review after your session.
            </div>

            <hr />

            <div className={styles["note-template__header"]}>
              Progress Note Template
            </div>
            <DropdownToggle
              menuClassName={styles["note-template__menu"]}
              titleClassName={styles["note-template__title"]}
              title={
                <div>
                  <OutlineDocumentFilled />
                  <input
                    type="text"
                    placeholder="Choose from below template"
                    onChange={(event) => {
                      setNoteSchema(undefined);
                      setNoteTemplateSearch(event.currentTarget.value);
                    }}
                    value={
                      currSelectedNoteSchema
                        ? currSelectedNoteSchema.title
                        : noteTemplateSearch
                    }
                  />
                </div>
              }
              Caret={<OutlineChevronDown />}
            >
              {filtered.map((el) => (
                <Dropdown.Item
                  key={el.note_schema_id}
                  onClick={() => {
                    setNoteSchema(el);
                  }}
                >
                  {el.title}
                </Dropdown.Item>
              ))}
            </DropdownToggle>
          </div>
        }
        labelConfirm={"End session"}
        labelCancel={"Cancel"}
        show={showConfirmEndModal}
        onCancel={() => setShowConfirmEndModal(false)}
        onConfirm={() => {
          onConfirmEndSession();
        }}
        footerClass={styles["confirm-end__footer"]}
      />
      {uploadError ? (
        <DataProcessingModal
          labelTitle="Upload failed"
          children={
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "2rem",
              }}
            >
              A problem occurred while uploading the recording. Please try again
              or save the recording file to your device and upload it later.
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "flex-end",
                  gap: "1rem",
                }}
              >
                <Button
                  onClick={() => {
                    setUploadError(null);
                    completeUpload();
                  }}
                >
                  Try again
                </Button>
                <Button
                  onClick={() => {
                    saveToDisk();
                    setTimeout(proceedToReview, 1000);
                  }}
                >
                  Dowload the recorded file
                </Button>
              </div>
            </div>
          }
          show={!!uploadError}
        />
      ) : isRecordingFinished ? (
        <DataProcessingModal
          labelTitle={"Uploading your session"}
          children={
            <div className={styles["audio-recorder__loading-wrapper"]}>
              <Loading />
            </div>
          }
          show={isRecordingFinished}
        />
      ) : (
        <>
          <ExternalAndInPersonModeRecorder
            appointment={appointment}
            showNotesPanel={showNotesPanel}
            isBackupWarningDismissed={isBackupWarningDismissed}
            setIsBackupWarningDismissed={setIsBackupWarningDismissed}
            recordingError={recordingError}
            mode={mode}
            isRecordingFinished={isRecordingFinished}
            isRecordingStarted={isRecordingStarted}
            isPaused={isPaused}
            recordingTimeString={recordingTimeString}
            mediaRecorderRef={mediaRecorderRef}
            setIsPaused={setIsPaused}
            patientAudioSource={patientAudioSource}
            setPatientAudioSource={setPatientAudioSource}
            setShowBrowserErrorBanner={setShowBrowserErrorBanner}
            setShowNotesPanel={setShowNotesPanel}
            initializedStreaming={initializedStreaming}
            startStreaming={startStreaming}
            getCurrentInputVolume={getCurrentInputVolume}
          />
        </>
      )}
      {showBrowserErrorBanner && (
        <ErrorBanner
          message={
            "To record external EHR sessions with headphones, please use Chrome or Edge."
          }
          onClose={() => setShowBrowserErrorBanner(false)}
          tryAgain={() => setShowBrowserErrorBanner(false)}
        />
      )}
      <AudioRecorderControlPanel
        appointment={appointment}
        currentTime={currentTime}
        isPaused={isPaused}
        mediaRecorderRef={mediaRecorderRef}
        setIsPaused={setIsPaused}
        setShowScheduleModal={setShowScheduleModal}
        isRecordingStarted={isRecordingStarted}
        isRecordingFinished={isRecordingFinished}
        setShowNotesPanel={setShowNotesPanel}
        setShowConfirmEndModal={setShowConfirmEndModal}
        micMediaStreamRef={micMediaStreamRef}
        hasMicroPhoneAccess={hasMicroPhoneAccess}
        onAudioInputChange={onAudioInputChange}
      />
    </div>
  );
};

export default AudioRecorder;
